import { useEffect, useRef } from "react";

type UseAutoMinimizeOptions = {
  isExpanded: boolean;
  onMinimize: () => void;
  delayMs?: number;
  disabled?: boolean;
};

/**
 * Highly performant custom hook that automatically calls `onMinimize` after `delayMs`
 * (default 5000ms) of user inactivity when `isExpanded` is true.
 *
 * Performance & Focus Protections:
 * - Uses pure `useRef` storage with 0 React state updates or re-render cycles.
 * - Pauses timer while any form control (<select>, <input>, etc.) within the container has focus.
 * - Pauses timer when `disabled` is true (e.g. during active Joyride tour).
 * - Uses throttled passive event listeners ({ passive: true }) to prevent main thread layout thrashing.
 */
export function useAutoMinimize<T extends HTMLElement = HTMLDivElement>({
  isExpanded,
  onMinimize,
  delayMs = 5000,
  disabled = false,
}: UseAutoMinimizeOptions) {
  const containerRef = useRef<T | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(0);
  const onMinimizeRef = useRef(onMinimize);

  // Keep latest onMinimize callback in ref without re-triggering effect subscriptions
  useEffect(() => {
    onMinimizeRef.current = onMinimize;
  }, [onMinimize]);

  // oxlint-disable-next-line react-doctor/effect-needs-cleanup
  useEffect(() => {
    if (!isExpanded || disabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return () => {};
    }

    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Do NOT start auto-minimize timer while focus is inside a form element (<select>, <input>) or container
      const activeEl = document.activeElement;
      if (activeEl && activeEl !== document.body) {
        const isFormControl =
          activeEl.tagName === "SELECT" ||
          activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          (containerRef.current && containerRef.current.contains(activeEl));

        if (isFormControl) {
          return;
        }
      }

      timerRef.current = setTimeout(() => {
        onMinimizeRef.current();
      }, delayMs);
    };

    // Initial timer start on expansion
    resetTimer();

    // Throttled activity handler (fires at most once per 200ms)
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > 200) {
        lastActivityRef.current = now;
        resetTimer();
      }
    };

    const target = containerRef.current ?? globalThis;

    target.addEventListener("mousemove", handleActivity, { passive: true });
    target.addEventListener("mouseenter", handleActivity, { passive: true });
    target.addEventListener("mousedown", handleActivity, { passive: true });
    target.addEventListener("click", handleActivity, { passive: true });
    target.addEventListener("keydown", handleActivity, { passive: true });
    target.addEventListener("change", handleActivity, { passive: true });
    target.addEventListener("input", handleActivity, { passive: true });
    target.addEventListener("focusin", handleActivity, { passive: true });
    target.addEventListener("focusout", handleActivity, { passive: true });
    target.addEventListener("touchstart", handleActivity, { passive: true });
    target.addEventListener("scroll", handleActivity, { capture: true, passive: true });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      target.removeEventListener("mousemove", handleActivity);
      target.removeEventListener("mouseenter", handleActivity);
      target.removeEventListener("mousedown", handleActivity);
      target.removeEventListener("click", handleActivity);
      target.removeEventListener("keydown", handleActivity);
      target.removeEventListener("change", handleActivity);
      target.removeEventListener("input", handleActivity);
      target.removeEventListener("focusin", handleActivity);
      target.removeEventListener("focusout", handleActivity);
      target.removeEventListener("touchstart", handleActivity);
      target.removeEventListener("scroll", handleActivity, { capture: true });
    };
  }, [isExpanded, delayMs, disabled]);

  return containerRef;
}
